<template>
  <div
    class="modal"
    :class="{ 'is-active': isActive }"
  >
    <div
      class="modal-background"
      @click="handleCancel"
    />
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ editMode ? 'Edit Property' : 'Add New Property' }}
        </p>
        <button
          class="delete"
          aria-label="close"
          @click="handleCancel"
        />
      </header>
      <section class="modal-card-body">
        <callout type="warning">Please create a bank account before adding your property.</callout>
        <!-- Property Information Section -->
        <div class="field">
          <label class="label">Street Address 1 *</label>
          <div class="control">
            <input
              v-model="formData.property_address_street_1"
              class="input"
              type="text"
              placeholder="123 Main Street"
              :class="{ 'is-danger': errors.property_address_street_1 }"
            >
          </div>
          <p
            v-if="errors.property_address_street_1"
            class="help is-danger"
          >
            {{ errors.property_address_street_1 }}
          </p>
        </div>

        <!-- City and ZIP Code Row -->
        <div class="columns is-variable is-1">
          <div class="column is-half">
            <div class="field">
              <label class="label">City *</label>
              <div class="control">
                <input
                  v-model="formData.property_address_city"
                  class="input"
                  type="text"
                  placeholder="Philadelphia"
                  :class="{ 'is-danger': errors.property_address_city }"
                >
              </div>
              <p
                v-if="errors.property_address_city"
                class="help is-danger"
              >
                {{ errors.property_address_city }}
              </p>
            </div>
          </div>

          <div class="column is-half">
            <div class="field">
              <label class="label">ZIP Code *</label>
              <div class="control">
                <input
                  v-model="formData.property_address_postal_code"
                  class="input"
                  type="text"
                  placeholder="19103"
                  maxlength="5"
                  :class="{ 'is-danger': errors.property_address_postal_code }"
                >
              </div>
              <p
                v-if="errors.property_address_postal_code"
                class="help is-danger"
              >
                {{ errors.property_address_postal_code }}
              </p>
            </div>
          </div>
        </div>

        <!-- State on its own row -->
        <div class="field">
          <label class="label">State *</label>
          <div class="control">
            <dropdown
              v-model="formData.property_address_state"
              :options="stateOptions"
              placeholder="Select state"
              text-key="text"
              value-key="value"
              :error="errors.property_address_state"
            />
          </div>
          <p
            v-if="errors.property_address_state"
            class="help is-danger"
          >
            {{ errors.property_address_state }}
          </p>
        </div>

        <!-- Property Type Dropdown -->
        <div class="field">
          <label class="label">Property Type *</label>
          <div class="control">
            <dropdown
              v-model="formData.property_type"
              :options="propertyTypeOptions"
              placeholder="Select property type"
              text-key="text"
              value-key="value"
              :error="errors.property_type"
            />
          </div>
          <p
            v-if="errors.property_type"
            class="help is-danger"
          >
            {{ errors.property_type }}
          </p>
        </div>

        <!-- Units Field -->
        <div class="field">
          <label class="label">Units *</label>
          <div class="control">
            <input
              v-model.number="formData.units"
              class="input"
              type="number"
              min="1"
              placeholder="Enter number of units"
              :class="{ 'is-danger': errors.units }"
            >
          </div>
          <p
            v-if="errors.units"
            class="help is-danger"
          >
            {{ errors.units }}
          </p>
        </div>

        <!-- Payment Method Selection -->
        <div class="field">
          <label class="label">Select Bank Account for Direct Deposit *</label>
          <div class="control">
            <p v-if="paymentMethodOptions.length === 0" class="help is-danger">
              You have no associated bank accounts. Please add bank information before proceeding.
            </p>
            <dropdown
              v-else
              v-model="formData.related_bank_info"
              :options="paymentMethodOptions"
              placeholder="Select a bank account"
              text-key="text"
              value-key="value"
              :error="errors.related_bank_info"
            />
          </div>
          <p
            v-if="errors.related_bank_info"
            class="help is-danger"
          >
            {{ errors.related_bank_info }}
          </p>
        </div>

        <!-- Authorized Representative Selection -->
        <div class="field" style="margin-top: 1.5rem;">
          <label class="label">
            Authorized Representative
            <span v-if="!formData.related_ar">(Optional)</span>
          </label>
          <div class="control">
            <dropdown
              v-model="formData.related_ar"
              :options="authorizedRepOptions"
              placeholder="Select an authorized representative"
              text-key="text"
              value-key="value"
            />
          </div>
        </div>

        <!-- Property Documents Section -->
        <hr />
        <h4 class="title is-5">
          Proof of Property Ownership and/or Authorization Documents
          <span v-if="isArNewlySelected" class="has-text-love-park-red">*</span>
        </h4>

        <div class="field">
          <p class="help">
            Documents need to be in PDF, JPG, or PNG format and must be less than 4.5MB in size.
          </p>
          <div class="file has-name" :class="{ 'is-primary': uploadedFiles.length > 0 }">
            <label class="file-label">
              <input
                ref="fileInput"
                class="file-input"
                type="file"
                accept="image/png,image/jpeg,application/pdf"
                multiple
                @change="handleFileSelect"
              >
              <span class="file-cta">
                <span class="file-icon">
                  <i class="fas fa-upload" />
                </span>
                <span class="file-label">
                  Choose file(s)...
                </span>
              </span>
              <span
                v-if="uploadedFiles.length > 0"
                class="file-name"
              >
                {{ uploadedFiles.length }} file(s) selected
              </span>
            </label>
          </div>

          <!-- File list -->
          <div v-if="uploadedFiles.length > 0" class="file-grid" style="margin-top: 1rem;">
            <div class="columns is-multiline">
              <div
                v-for="(file, index) in uploadedFiles"
                :key="file.id"
                class="column is-half"
              >
                <div class="box" style="padding: 0.75rem; margin-bottom: 0;">
                  <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;">
                    <div style="display: flex; align-items: center; overflow: hidden; flex: 1;">
                      <i class="fas fa-file" style="margin-right: 0.5rem; color: #2176d2; flex-shrink: 0;"></i>
                      <span class="is-size-7" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ file.name }}</span>
                    </div>
                    <button
                      class="delete is-small"
                      type="button"
                      @click="removeFile(index)"
                      aria-label="Remove file"
                      style="flex-shrink: 0; margin-left: 0.5rem;"
                    ></button>
                  </div>
                  <div class="field" style="margin-bottom: 0;">
                    <div class="control">
                      <div class="select is-small is-fullwidth">
                        <select v-model="fileDocumentTypes[index]" :class="{ 'is-danger': errors[`fileType_${index}`] }">
                          <option value="">Select type...</option>
                          <option
                            v-for="docType in authDocumentTypeOptions"
                            :key="docType.value"
                            :value="docType.value"
                          >
                            {{ docType.text }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <p v-if="errors[`fileType_${index}`]" class="help is-danger is-size-7" style="margin-top: 0.25rem;">
                    {{ errors[`fileType_${index}`] }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <p
            v-if="errors.files"
            class="help is-danger"
          >
            {{ errors.files }}
          </p>
        </div>
      </section>
      <footer
        class="modal-card-foot"
        style="display: flex; flex-direction: column; align-items: flex-start;"
      >
        <!-- Loading Message -->
        <div
          v-if="isSubmitting && loadingMessage"
          class="notification has-background-ben-franklin-blue-light"
          style="margin-bottom: 1rem; width: 100%;"
        >
          <p class="has-text-dark-ben-franklin">
            <span class="icon">
              <i class="fas fa-spinner fa-pulse" />
            </span>
            {{ loadingMessage }}
          </p>
        </div>

        <!-- Error Message in Footer -->
        <div
          v-if="errors.general"
          class="notification has-background-love-park-red-light"
          style="margin-bottom: 1rem; width: 100%;"
        >
          <p class="has-text-love-park-red">
            {{ errors.general }}
          </p>
        </div>

        <div class="buttons">
          <button
            class="button is-primary"
            :class="{ 'is-loading': isSubmitting }"
            @click="handleSubmit"
          >
            <span class="icon">
              <i class="fas fa-check" />
            </span>
            <span>{{ editMode ? 'Update Property' : 'Add Property' }}</span>
          </button>
          <button
            class="button is-secondary"
            :disabled="isSubmitting"
            @click="handleCancel"
          >
            Cancel
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import options from "@/utilities/options.js";
import paymentMethods from "../mock-data/payments";

export default {
  name: "AddPropertyModal",
  components: {},
  mixins: [options],
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    propertyData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      formData: {
        property_address_street_1: "",
        property_address_city: "",
        property_address_state: "Pennsylvania",
        property_address_postal_code: "",
        property_address_country: "US",
        property_type: "",
        units: "",
        related_bank_info: null,
        related_ar: null,
      },
      uploadedFiles: [],
      fileDocumentTypes: [],
      originalRelatedAr: null,
      authDocumentTypeOptions: [
        { value: "Property Management Agreement", text: "Property Management Agreement" },
        { value: "Articles of Organization or EIN", text: "Articles of Organization or EIN" },
        { value: "IRS CP 575 Confirmation Letter", text: "IRS CP 575 Confirmation Letter" },
        { value: "Power of Attorney", text: "Power of Attorney" },
        { value: "Receivership Certificate", text: "Receivership Certificate" },
      ],
      errors: {},
      isSubmitting: false,
      loadingMessage: '',
      nextFileId: 0,
      closeTimeout: null,
    };
  },
  watch: {
    propertyData: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.editMode) {
          this.populateFormForEdit(newVal);
        }
      },
    },
    isActive(newVal) {
      if (!newVal) {
        // Reset form when modal closes
        this.resetForm();
      } else if (this.editMode && this.propertyData) {
        // Populate form when modal opens in edit mode
        this.populateFormForEdit(this.propertyData);
      }
    },
  },
  computed: {
    ...mapGetters("dashboard", ["authorizedReps", "payments"]),
    stateOptions() {
      // Transform States array from options mixin into dropdown format
      return this.States.map(state => ({
        value: state,
        text: state,
      }));
    },
    propertyTypeOptions() {
      return [
        { value: "Single property", text: "Single property" },
        { value: "Multi-unit property", text: "Multi-unit property" },
        { value: "Single unit in a multi-unit property", text: "Single unit in a multi-unit property" },
      ];
    },
    paymentMethodOptions() {
      // Only show approved payment methods
      return this.payments
        .filter(pm => pm.bank_info_status !== 'Inactive')
        .map(pm => ({
          value: pm.rid,
          text: `${pm.bank_name} (...${pm.last_four_account_number})`,
        }));
    },
    authorizedRepOptions() {
      // Only show active representatives
      return this.authorizedReps
        .filter(rep => rep.status === 'Active')
        .map(rep => ({
          value: rep.id || rep.rid,
          text: `${rep.ar_full_name} - ${rep.ar_email}`,
        }));
    },
    isArNewlySelected() {
      // In new property mode, AR is newly selected if it has a value
      if (!this.editMode) {
        return !!this.formData.related_ar;
      }
      // In edit mode, AR is newly selected if:
      // - Previously no AR, now has AR, OR
      // - AR changed to a different value
      const hadAr = !!this.originalRelatedAr;
      const hasAr = !!this.formData.related_ar;

      if (!hadAr && hasAr) return true;  // Newly added
      if (hadAr && hasAr && this.originalRelatedAr !== this.formData.related_ar) return true;  // Changed
      return false;
    },
  },
  methods: {
    populateFormForEdit(property) {
      // Map property fields to form data
      this.formData.property_address_street_1 = property.property_address_street_1 || '';
      this.formData.property_address_city = property.property_address_city || '';
      this.formData.property_address_state = property.property_address_state || '';
      this.formData.property_address_postal_code = property.property_address_postal_code || '';
      this.formData.property_address_country = property.property_address_country || 'US';
      this.formData.property_type = property.property_type || '';
      this.formData.units = property.units || '';

      // Set related bank info - handle string, number, and empty string
      if (property.related_bank_info) {
        this.formData.related_bank_info = property.related_bank_info;
      } else {
        this.formData.related_bank_info = null;
      }
      
      // Set authorized representative - handle string, number, and empty string
      if (property.related_ar) {
        this.formData.related_ar = property.related_ar;
      } else {
        this.formData.related_ar = null;
      }

      // Save original AR value for change detection
      this.originalRelatedAr = property.related_ar || null;

      // Note: We don't populate files as they can't be pre-filled for security reasons
    },
    validateForm() {
      this.errors = {};
      let isValid = true;

      // Street Address validation
      if (!this.formData.property_address_street_1 || this.formData.property_address_street_1.trim() === "") {
        this.errors.property_address_street_1 = "Street address is required";
        isValid = false;
      }

      // City validation
      if (!this.formData.property_address_city || this.formData.property_address_city.trim() === "") {
        this.errors.property_address_city = "City is required";
        isValid = false;
      }

      // State validation
      if (!this.formData.property_address_state) {
        this.errors.property_address_state = "State is required";
        isValid = false;
      }

      // ZIP Code validation
      if (!this.formData.property_address_postal_code || this.formData.property_address_postal_code.trim() === "") {
        this.errors.property_address_postal_code = "ZIP code is required";
        isValid = false;
      } else if (!/^\d{5}$/.test(this.formData.property_address_postal_code)) {
        this.errors.property_address_postal_code = "ZIP code must be 5 digits";
        isValid = false;
      }

      // Property Type validation
      if (!this.formData.property_type) {
        this.errors.property_type = "Property type is required";
        isValid = false;
      }

      // Units validation
      if (!this.formData.units) {
        this.errors.units = "Number of units is required";
        isValid = false;
      } else if (!Number(this.formData.units) || Number(this.formData.units) < 1) {
        this.errors.units = "Units must be a positive whole number";
        isValid = false;
      }

      // Payment method validation
      if (!this.formData.related_bank_info) {
        this.errors.related_bank_info = "Payment method is required";
        isValid = false;
      }

      // Authorization document validation (only when AR is newly selected)
      if (this.isArNewlySelected) {
        // Check minimum files
        if (this.uploadedFiles.length < 1) {
          this.errors.files = "At least 1 authorization document is required when assigning an Authorized Representative.";
          isValid = false;
        }
        // Check maximum files
        else if (this.uploadedFiles.length > 2) {
          this.errors.files = "Maximum of 2 authorization documents allowed.";
          isValid = false;
        }
        // Check document types are selected
        else {
          for (let i = 0; i < this.uploadedFiles.length; i++) {
            if (!this.fileDocumentTypes[i]) {
              this.errors[`fileType_${i}`] = "Please select a document type";
              isValid = false;
            }
          }
        }
      }

      return isValid;
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;
      this.loadingMessage = this.editMode ? 'Updating property...' : 'Creating property...';

      try {
        const userInfo = this.$store.getters['auth/userInfo'];
        const organization_rid = userInfo?.qbUser?.related_organization;

        if (!organization_rid) {
          this.errors.general = 'Unable to determine your organization. Please try again.';
          this.isSubmitting = false;
          return;
        }

        // Prepare property data
        const propertyData = {
          property_address_street_1: this.formData.property_address_street_1,
          property_address_city: this.formData.property_address_city,
          property_address_state: this.formData.property_address_state,
          property_address_postal_code: this.formData.property_address_postal_code,
          property_address_country: this.formData.property_address_country,
          property_type: this.formData.property_type,
          units: this.formData.units,
          related_bank_info: this.formData.related_bank_info,
          related_ar: this.formData.related_ar || "",
        };

        // Include rid for updates - QuickBase will handle the upsert
        if (this.editMode && this.propertyData?.rid) {
          propertyData.rid = this.propertyData.rid;
        }

        // Submit property (creates or updates based on presence of rid)
        const propertyResult = await this.$store.dispatch('dashboard/submitProperty', {
          organization_rid,
          propertyData,
        });

        if (!propertyResult.success) {
          this.errors.general = propertyResult.message;
          this.isSubmitting = false;
          return;
        }

        const propertyRid = propertyResult.property.rid;

        // Step 2: Upload files if any
        if (this.uploadedFiles.length > 0) {
          this.loadingMessage = 'Uploading files...';

          const fileUploads = await Promise.all(
            this.uploadedFiles.map(async (file, index) => {
              const base64Data = await this.getBase64(file.file);
              // Use selected document type if available, otherwise default to Property Authorization Form
              const documentType = this.fileDocumentTypes[index] || "Property Authorization Form";

              return this.$store.dispatch('dashboard/uploadFileForProperty', {
                propertyRid,
                file: {
                  name: file.name,
                  base64Data,
                },
                documentType
              });
            }),
          );
          console.log(`Submitting ${fileUploads.length} files`)
          const failedUploads = fileUploads.filter(result => !result.success);
          if (failedUploads.length > 0) {
            const action = this.editMode ? 'updated' : 'created';
            const errorMessages = failedUploads.map(f => f.message).join('; ');
            this.errors.general = `Property ${action}, but file upload failed: ${errorMessages}. This dialog will close in 5 seconds.`;
            this.isSubmitting = false;
            this.loadingMessage = '';
            // Show error for 5 seconds, then close modal
            this.closeTimeout = setTimeout(() => {
              this.$emit("submit", propertyResult.property);
              this.resetForm();
            }, 5000);
            return;
          }
        }

        // Success!
        this.$emit("submit", propertyResult.property);
        this.resetForm();

      } catch (error) {
        console.error('Unexpected error with property:', error);
        this.errors.general = 'An unexpected error occurred. Please try again.';
      } finally {
        this.isSubmitting = false;
        this.loadingMessage = '';
      }
    },
    handleCancel() {
      this.$emit("close");
      this.resetForm();
    },
    resetForm() {
      // Clear any pending close timeout
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }

      this.formData = {
        property_address_street_1: "",
        property_address_city: "",
        property_address_state: "Pennsylvania",
        property_address_postal_code: "",
        property_address_country: "US",
        property_type: "",
        units: "",
        related_bank_info: null,
        related_ar: null,
      };
      this.uploadedFiles = [];
      this.fileDocumentTypes = [];
      this.originalRelatedAr = null;
      this.errors = {};
      this.nextFileId = 0;

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    async handleFileSelect(event) {
      const files = Array.from(event.target.files);

      for (const file of files) {
        // Check max file limit (2 files when AR is selected)
        if (this.isArNewlySelected && this.uploadedFiles.length >= 2) {
          this.errors.files = "Maximum of 2 authorization documents allowed.";
          break;
        }

        // Validate file type
        const validTypes = ['image/png', 'image/jpeg', 'application/pdf'];
        const validExtensions = ['png', 'jpg', 'jpeg', 'pdf'];
        const fileExtension = file.name.split('.').pop().toLowerCase();

        if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
          this.errors.files = `Invalid file type: ${file.name}. Only PDF, PNG, and JPEG files are accepted.`;
          continue;
        }

        // Validate file size (4.5MB)
        const maxSize = 4.5 * 1024 * 1024;
        if (file.size > maxSize) {
          this.errors.files = `File too large: ${file.name}. Maximum size is 4.5MB.`;
          continue;
        }

        // Add file to list
        this.uploadedFiles.push({
          id: this.nextFileId++,
          name: file.name,
          size: file.size,
          type: file.type,
          file: file,
        });

        // Initialize document type as unselected
        this.fileDocumentTypes.push("");
      }

      // Clear input
      event.target.value = '';

      // Clear error
      if (this.uploadedFiles.length > 0) {
        this.errors.files = null;
      }
    },
    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
      this.fileDocumentTypes.splice(index, 1);
    },
    async getBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          let encoded = reader.result.toString().replace(/^data:(.*,)?/, '');
          if ((encoded.length % 4) > 0) {
            encoded += '='.repeat(4 - (encoded.length % 4));
          }
          resolve(encoded);
        };
        reader.onerror = error => reject(error);
      });
    },
    formatFileSize(bytes) {
      if (bytes === 0) {
        return "0 Bytes";
      }
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
    },
  },
};
</script>

<style scoped>
.medium-gray {
  color: #a1a1a1; /* Medium Gray - Philly Style Guide */
}

/* Adjust modal z-index to be above dashboard header (z-index: 1001) */
.modal {
  z-index: 1003;
}

.modal-background {
  z-index: 1003;
}

.modal-card {
  max-height: calc(100vh - 90px); /* Account for header (70px) + padding */
  margin-top: 80px; /* Position below header */
  z-index: 1004;
}

.modal-card-body {
  max-height: calc(100vh - 240px); /* Account for header + modal header + modal footer */
  overflow-y: auto;
}

.modal-card-foot {
  justify-content: flex-start;
}

/* Form field spacing */
.field {
  margin-bottom: 1rem;
}

.columns {
  margin-bottom: 0;
}

.columns .column .field {
  margin-bottom: 0;
}

/* Ensure consistent label height across columns */
.label {
  min-height: 1.5rem;
  display: block;
}

input::placeholder {
  color: #999999 !important;
  opacity: 1 !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modal-card {
    margin-top: 70px; /* Account for smaller header on mobile (60px) */
    max-height: calc(100vh - 80px);
  }

  .modal-card-body {
    max-height: calc(100vh - 200px);
  }
}

hr {
  background-color: #cfcfcf; /* Sidewalk Gray - Philly Style Guide */
  height: 1px;
  margin: 1.5rem 0;
}
</style>
